import { useState, useEffect } from "react";
import { useSearchParams, Navigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import {
  LayoutDashboard,
  Calendar,
  CalendarDays,
  Users,
  Settings2,
  Plus,
  Flag,
  Trash2,
} from "lucide-react";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Skeleton } from "@/components/ui/skeleton";
import { Switch } from "@/components/ui/switch";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { DatePicker } from "@/components/ui/DatePicker";
import { AppLayout } from "@/components/AppLayout";
import { LeaveListView } from "@/components/leave/LeaveListView";
import { LeaveCalendar } from "@/components/leave/LeaveCalendar";
import { LeaveDetailModal } from "@/components/leave/LeaveDetailModal";
import { LeaveTypeManager } from "@/components/leave/LeaveTypeManager";
import { ManualLeaveRequestDialog } from "@/components/leave/ManualLeaveRequestDialog";
import { LeaveDashboard } from "@/components/leave/LeaveDashboard";
import { TeamAvailabilityIndicator } from "@/components/leave/TeamAvailabilityIndicator";
import { useAuth } from "@/contexts/AuthContext";
import {
  apiGetLeaveRequests,
  apiGetLeaveTypes,
  apiGetLeaveRequest,
  apiGetAllPublicHolidays,
  apiAddPublicHoliday,
  apiBulkImportPublicHolidays,
  apiUpdatePublicHoliday,
  apiDeletePublicHoliday,
} from "@/lib/api";
import { format, parseISO } from "date-fns";
import { Button } from "@/components/ui/button";

const VALID_TABS = [
  "dashboard",
  "calendar",
  "requests",
  "settings",
  "holidays",
] as const;
type TabValue = (typeof VALID_TABS)[number];

const CURRENT_YEAR = new Date().getFullYear();
const CALENDAR_COUNTRY = "North Macedonia";

function isWeekendDate(dateStr: string): boolean {
  const d = parseISO(dateStr);
  return d.getDay() === 0 || d.getDay() === 6;
}

function HolidaysTab() {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const [holidayTab, setHolidayTab] = useState<"list" | "add" | "bulk">("list");
  const [newHoliday, setNewHoliday] = useState({
    date: format(new Date(), "yyyy-MM-dd"),
    name: "",
  });
  const [bulkText, setBulkText] = useState("");
  const [filterYear, setFilterYear] = useState(CURRENT_YEAR);

  const { data: allHolidays = [], isLoading } = useQuery({
    queryKey: ["publicHolidaysAll", CALENDAR_COUNTRY],
    queryFn: () => apiGetAllPublicHolidays(CALENDAR_COUNTRY),
  });

  const displayedHolidays = allHolidays
    .filter((h) => h.year === filterYear)
    .sort((a, b) => a.date.localeCompare(b.date));

  const addMutation = useMutation({
    mutationFn: apiAddPublicHoliday,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["publicHolidaysAll"] });
      qc.invalidateQueries({ queryKey: ["publicHolidays"] });
      toast.success(t("leave.holidays.added"));
      setNewHoliday({ date: format(new Date(), "yyyy-MM-dd"), name: "" });
      setHolidayTab("list");
    },
    onError: (e: any) => toast.error(e.message ?? t("leave.holidays.addFailed")),
  });

  const bulkMutation = useMutation({
    mutationFn: ({ country, holidays }: { country: string; holidays: { date: string; name: string }[] }) =>
      apiBulkImportPublicHolidays(country, holidays),
    onSuccess: (res) => {
      qc.invalidateQueries({ queryKey: ["publicHolidaysAll"] });
      qc.invalidateQueries({ queryKey: ["publicHolidays"] });
      toast.success(t("leave.holidays.imported", { count: res.inserted }));
      setBulkText("");
      setHolidayTab("list");
    },
    onError: (e: any) => toast.error(e.message ?? t("leave.holidays.importFailed")),
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: number; data: { next_monday_off?: boolean; name?: string } }) =>
      apiUpdatePublicHoliday(id, data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["publicHolidaysAll"] });
      qc.invalidateQueries({ queryKey: ["publicHolidays"] });
    },
    onError: (e: any) => toast.error(e.message ?? t("leave.holidays.updateFailed")),
  });

  const deleteMutation = useMutation({
    mutationFn: apiDeletePublicHoliday,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["publicHolidaysAll"] });
      qc.invalidateQueries({ queryKey: ["publicHolidays"] });
      toast.success(t("leave.holidays.deleted"));
    },
    onError: (e: any) => toast.error(e.message ?? t("leave.holidays.deleteFailed")),
  });

  function handleAddSingle() {
    if (!newHoliday.name.trim() || !newHoliday.date) return;
    addMutation.mutate({ country: CALENDAR_COUNTRY, date: newHoliday.date, name: newHoliday.name.trim() });
  }

  function handleBulkImport() {
    const lines = bulkText.split("\n").map((l) => l.trim()).filter(Boolean);
    const parsed: { date: string; name: string }[] = [];
    for (const line of lines) {
      const idx = line.indexOf(",");
      if (idx === -1) continue;
      const date = line.slice(0, idx).trim();
      const name = line.slice(idx + 1).trim();
      if (date && name) parsed.push({ date, name });
    }
    if (parsed.length === 0) { toast.error(t("leave.holidays.noValidLines")); return; }
    bulkMutation.mutate({ country: CALENDAR_COUNTRY, holidays: parsed });
  }

  const tabCls = "flex items-center gap-1.5 text-sm rounded-none border-b-2 border-transparent px-4 py-2.5 -mb-px data-[state=active]:border-primary data-[state=active]:text-foreground data-[state=active]:shadow-none text-muted-foreground hover:text-foreground transition-colors bg-transparent";

  return (
    <div className="rounded-2xl border border-border/60 bg-card shadow-sm overflow-hidden">
      <div className="px-5 py-3.5 border-b border-border/60 flex items-center justify-between flex-wrap gap-2">
        <div className="flex items-center gap-2">
          <Flag className="h-4 w-4 text-red-500" />
          <h2 className="text-sm font-semibold text-foreground">
            {CALENDAR_COUNTRY} — {t("leave.holidays.title")}
          </h2>
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant={filterYear === CURRENT_YEAR ? "default" : "outline"}
            size="sm"
            className="h-7 text-xs"
            onClick={() => setFilterYear(CURRENT_YEAR)}
          >
            {CURRENT_YEAR}
          </Button>
          <Button
            variant={filterYear === CURRENT_YEAR + 1 ? "default" : "outline"}
            size="sm"
            className="h-7 text-xs"
            onClick={() => setFilterYear(CURRENT_YEAR + 1)}
          >
            {CURRENT_YEAR + 1}
          </Button>
        </div>
      </div>

      <div className="border-b border-border/60">
        <div className="flex">
          {(["list", "add", "bulk"] as const).map((tab) => (
            <button
              key={tab}
              onClick={() => setHolidayTab(tab)}
              className={`${tabCls} ${holidayTab === tab ? "border-primary text-foreground" : ""}`}
            >
              {tab === "list" ? t("leave.holidays.tabList") : tab === "add" ? t("leave.holidays.tabAdd") : t("leave.holidays.tabBulk")}
            </button>
          ))}
        </div>
      </div>

      <div className="p-5">
        {holidayTab === "list" && (
          <div>
            <div className="flex items-center justify-between mb-3">
              <p className="text-xs text-muted-foreground italic">
                {t("leave.holidays.monOffHint")}
              </p>
              <span className="text-xs text-muted-foreground font-medium">{t("leave.holidays.holidayCount", { count: displayedHolidays.length })}</span>
            </div>
            {isLoading ? (
              <div className="space-y-2">
                {[1, 2, 3].map((i) => <Skeleton key={i} className="h-10 rounded-lg" />)}
              </div>
            ) : displayedHolidays.length === 0 ? (
              <div className="py-10 text-center text-sm text-muted-foreground">
                {t("leave.holidays.noHolidays", { year: filterYear })}
              </div>
            ) : (
              <div className="space-y-1">
                {displayedHolidays.map((h) => {
                  const weekend = isWeekendDate(h.date);
                  const dayLabel = parseISO(h.date).getDay() === 6 ? "Sat" : "Sun";
                  return (
                    <div
                      key={h.id}
                      className={`flex items-center justify-between py-2 px-2.5 rounded-lg group transition-colors ${
                        weekend
                          ? "bg-amber-50/60 dark:bg-amber-950/10 hover:bg-amber-50 dark:hover:bg-amber-950/20"
                          : "hover:bg-muted/50"
                      }`}
                    >
                      <div className="flex items-center gap-3 flex-1 min-w-0">
                        <span className="text-xs font-mono text-muted-foreground w-24 shrink-0">{h.date}</span>
                        <span className="text-sm text-foreground truncate">{h.name}</span>
                        {weekend && (
                          <span className="text-[10px] font-medium px-1.5 py-0.5 rounded bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400 shrink-0">
                            {dayLabel}
                          </span>
                        )}
                      </div>
                      <div className="flex items-center gap-2 ml-2 shrink-0">
                        {weekend && (
                          <div className="flex items-center gap-1.5">
                            <span className="text-xs text-muted-foreground">{t("leave.holidays.monOffToggle")}</span>
                            <Switch
                              checked={!!h.next_monday_off}
                              onCheckedChange={(checked) =>
                                updateMutation.mutate({ id: h.id, data: { next_monday_off: checked } })
                              }
                              className="scale-75"
                            />
                          </div>
                        )}
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-6 w-6 opacity-0 group-hover:opacity-100 text-destructive hover:text-destructive"
                          onClick={() => deleteMutation.mutate(h.id)}
                        >
                          <Trash2 className="h-3.5 w-3.5" />
                        </Button>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}

        {holidayTab === "add" && (
          <div className="max-w-sm space-y-4">
            <div className="space-y-1.5">
              <Label>{t("leave.holidays.calendar")}</Label>
              <div className="flex items-center gap-2 px-3 py-2 rounded-lg border border-border bg-muted/30 text-sm text-foreground">
                <Flag className="h-3.5 w-3.5 text-red-500 shrink-0" />
                {CALENDAR_COUNTRY}
              </div>
            </div>
            <div className="space-y-1.5">
              <Label>{t("leave.holidays.date")}</Label>
              <DatePicker
                value={newHoliday.date}
                onChange={(v) => setNewHoliday((p) => ({ ...p, date: v }))}
              />
            </div>
            <div className="space-y-1.5">
              <Label>{t("leave.holidays.holidayName")}</Label>
              <Input
                value={newHoliday.name}
                onChange={(e) => setNewHoliday((p) => ({ ...p, name: e.target.value }))}
                placeholder={t("leave.holidays.holidayNamePlaceholder")}
              />
            </div>
            <Button
              onClick={handleAddSingle}
              disabled={!newHoliday.name.trim() || !newHoliday.date || addMutation.isPending}
              className="w-full gradient-brand text-primary-foreground"
            >
              <Plus className="h-4 w-4 mr-1.5" />
              {t("leave.holidays.addHoliday")}
            </Button>
          </div>
        )}

        {holidayTab === "bulk" && (
          <div className="max-w-sm space-y-4">
            <div className="space-y-1.5">
              <Label>{t("leave.holidays.calendar")}</Label>
              <div className="flex items-center gap-2 px-3 py-2 rounded-lg border border-border bg-muted/30 text-sm text-foreground">
                <Flag className="h-3.5 w-3.5 text-red-500 shrink-0" />
                {CALENDAR_COUNTRY}
              </div>
            </div>
            <div className="space-y-1.5">
              <Label>{t("leave.holidays.pasteHolidays")}</Label>
              <p className="text-xs text-muted-foreground">
                {t("leave.holidays.pasteHint")} <code className="bg-muted px-1 rounded">YYYY-MM-DD, Holiday Name</code>
              </p>
              <Textarea
                value={bulkText}
                onChange={(e) => setBulkText(e.target.value)}
                placeholder={"2026-12-25, Christmas Day\n2026-12-26, Boxing Day"}
                rows={8}
                className="font-mono text-sm"
              />
            </div>
            <Button
              onClick={handleBulkImport}
              disabled={!bulkText.trim() || bulkMutation.isPending}
              className="w-full gradient-brand text-primary-foreground"
            >
              <Plus className="h-4 w-4 mr-1.5" />
              {t("leave.holidays.importHolidays")}
            </Button>
          </div>
        )}
      </div>
    </div>
  );
}

// ── Component ──────────────────────────────────────────────────────────────────

const TeamLeave = () => {
  const { t } = useTranslation();
  const { user } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();

  const isAdminOrHr =
    user?.role.mainRole === "admin" || user?.role.mainRole === "hr";
  const isLineManager = !!(
    user?.isLineManager || user?.role.subRole === "line_manager"
  );
  const canAccessTeamLeave = isAdminOrHr || isLineManager;
  const canManageSettings = isAdminOrHr;

  // Tab from URL param
  const tabParam = searchParams.get("tab") as TabValue | null;
  const [activeTab, setActiveTab] = useState<TabValue>(
    tabParam && (VALID_TABS as readonly string[]).includes(tabParam)
      ? (tabParam as TabValue)
      : "dashboard",
  );
  const [manualDialogOpen, setManualDialogOpen] = useState(false);

  // URL-param-driven modal (from notification clicks)
  const openRequestId = searchParams.get("openRequest");

  const { data: urlOpenRequest, isError: urlRequestError } = useQuery({
    queryKey: ["leaveRequest", openRequestId],
    queryFn: () => apiGetLeaveRequest(Number(openRequestId)),
    enabled: !!openRequestId,
    staleTime: 0,
    retry: false,
  });

  useEffect(() => {
    if (urlRequestError && openRequestId) {
      toast.error(t("leave.couldNotLoadRequest"));
      const params = new URLSearchParams(searchParams);
      params.delete("openRequest");
      setSearchParams(params, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [urlRequestError]);

  function handleUrlRequestClose() {
    const params = new URLSearchParams(searchParams);
    params.delete("openRequest");
    setSearchParams(params, { replace: true });
  }

  // Queries

  const { data: teamRequests = [], isLoading: teamLoading } = useQuery({
    queryKey: ["leaveRequests"],
    queryFn: () => apiGetLeaveRequests(),
  });

  const { data: leaveTypes = [] } = useQuery({
    queryKey: ["leaveTypes"],
    queryFn: apiGetLeaveTypes,
  });

  // Redirect unauthorized users to personal leave
  if (!canAccessTeamLeave) {
    return <Navigate to="/leave" replace />;
  }

  const pendingCount = teamRequests.filter(
    (r) => ["Pending", "HR Review Required", "Document Uploaded"].includes(r.status),
  ).length;

  // ── Subtitle ─────────────────────────────────────────────────────────────────

  const subtitle = isAdminOrHr
    ? t("leave.teamLeaveHrSubtitle")
    : t("leave.teamLeaveSubtitle");

  // ── Render ───────────────────────────────────────────────────────────────────

  return (
    <AppLayout>
      <div className="space-y-6 w-full">
        {/* Page header */}
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <div className="flex items-center gap-3">
            <div className="h-9 w-9 rounded-xl bg-teal-500/10 flex items-center justify-center shrink-0">
              <CalendarDays className="h-5 w-5 text-teal-600" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-foreground">
                {t("leave.teamLeaveTitle")}
              </h1>
              <p className="text-sm text-muted-foreground mt-1">{subtitle}</p>
            </div>
          </div>
          {isAdminOrHr && (
            <Button className="gap-2 self-start sm:self-auto" onClick={() => setManualDialogOpen(true)}>
              <Plus className="h-4 w-4" />
              {t("leave.createLeaveForEmployee")}
            </Button>
          )}
        </div>

        {/* Main tabs */}
        <Tabs
          value={activeTab}
          onValueChange={(v) => setActiveTab(v as TabValue)}
        >
          <TabsList className="w-full justify-start bg-transparent h-auto p-0 border-b border-border/60 rounded-none gap-0 overflow-x-auto overflow-y-hidden">
            <TabsTrigger
              value="dashboard"
              className="flex items-center gap-1.5 text-sm rounded-none border-b-2 border-transparent px-4 py-2.5 -mb-px data-[state=active]:border-primary data-[state=active]:text-foreground data-[state=active]:shadow-none text-muted-foreground hover:text-foreground transition-colors bg-transparent"
            >
              <LayoutDashboard className="h-3.5 w-3.5" />
              {t("leave.leaveDashboard")}
            </TabsTrigger>
            <TabsTrigger value="calendar" className="flex items-center gap-1.5 text-sm rounded-none border-b-2 border-transparent px-4 py-2.5 -mb-px data-[state=active]:border-primary data-[state=active]:text-foreground data-[state=active]:shadow-none text-muted-foreground hover:text-foreground transition-colors bg-transparent">
              <Calendar className="h-3.5 w-3.5" />
              {t("leave.calendar")}
            </TabsTrigger>
            <TabsTrigger value="requests" className="flex items-center gap-1.5 text-sm rounded-none border-b-2 border-transparent px-4 py-2.5 -mb-px data-[state=active]:border-primary data-[state=active]:text-foreground data-[state=active]:shadow-none text-muted-foreground hover:text-foreground transition-colors bg-transparent">
              <Users className="h-3.5 w-3.5" />
              {t("leave.requests")}
              {pendingCount > 0 && (
                <span className="ml-0.5 inline-flex h-4 w-4 items-center justify-center rounded-full bg-destructive text-destructive-foreground text-[10px] font-bold">
                  {pendingCount > 9 ? "9+" : pendingCount}
                </span>
              )}
            </TabsTrigger>
            {canManageSettings && (
              <TabsTrigger
                value="settings"
                className="flex items-center gap-1.5 text-sm rounded-none border-b-2 border-transparent px-4 py-2.5 -mb-px data-[state=active]:border-primary data-[state=active]:text-foreground data-[state=active]:shadow-none text-muted-foreground hover:text-foreground transition-colors bg-transparent"
              >
                <Settings2 className="h-3.5 w-3.5" />
                {t("leave.leaveSettings")}
              </TabsTrigger>
            )}
            {canManageSettings && (
              <TabsTrigger
                value="holidays"
                className="flex items-center gap-1.5 text-sm rounded-none border-b-2 border-transparent px-4 py-2.5 -mb-px data-[state=active]:border-primary data-[state=active]:text-foreground data-[state=active]:shadow-none text-muted-foreground hover:text-foreground transition-colors bg-transparent"
              >
                <Flag className="h-3.5 w-3.5 text-red-500" />
                Holidays
              </TabsTrigger>
            )}
          </TabsList>

          {/* ── Dashboard ── */}
          <TabsContent value="dashboard" className="space-y-8 mt-4">
            {/* Leave dashboard widgets */}
            <LeaveDashboard
              requests={teamRequests}
              isLoading={teamLoading}
              onClickPending={() => setActiveTab("requests")}
            />

            {/* Team availability */}
            <div>
              <h2 className="text-sm font-semibold text-muted-foreground uppercase tracking-wide mb-3">
                {t("leave.teamAvailabilityDetails")}
              </h2>
              <TeamAvailabilityIndicator
                requests={teamRequests}
                isLoading={teamLoading}
              />
            </div>
          </TabsContent>

          {/* ── Calendar ── */}
          <TabsContent value="calendar" className="mt-4">
            <LeaveCalendar />
          </TabsContent>

          {/* ── Requests ── */}
          <TabsContent value="requests" className="mt-4">
            {teamLoading ? (
              <Skeleton className="h-64 rounded-lg" />
            ) : (
              <LeaveListView
                requests={teamRequests}
                leaveTypes={leaveTypes}
                showEmployee
              />
            )}
          </TabsContent>

          {/* ── Settings (HR/Admin only) ── */}
          {canManageSettings && (
            <TabsContent value="settings" className="mt-4">
              <LeaveTypeManager />
            </TabsContent>
          )}

          {/* ── Holidays (HR/Admin only) ── */}
          {canManageSettings && (
            <TabsContent value="holidays" className="mt-4">
              <HolidaysTab />
            </TabsContent>
          )}
        </Tabs>

        {/* URL-param-driven detail modal (notification deep-link) */}
        <LeaveDetailModal
          request={urlOpenRequest ?? null}
          open={!!urlOpenRequest}
          onOpenChange={(v) => {
            if (!v) handleUrlRequestClose();
          }}
        />
        <ManualLeaveRequestDialog
          open={manualDialogOpen}
          onOpenChange={setManualDialogOpen}
        />
      </div>
    </AppLayout>
  );
};

export default TeamLeave;
